import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { CandidateDecisionArea } from '@/modules/candidate-detail/components/CandidateDecisionArea';
import { CandidateDraftReview } from '@/modules/candidate-detail/components/CandidateDraftReview';
import { CandidateSummary } from '@/modules/candidate-detail/components/CandidateSummary';
import type { CandidateBriefApi } from '@/modules/candidate-detail/queries/types';
import {
  candidateBriefQueryOptions,
  useCandidateBriefQuery,
} from '@/modules/candidate-detail/queries/useCandidateBriefQuery';
import type { PendingDecisionAction } from '@/modules/candidate-detail/types';
import {
  decisionFromStatus,
  mapCandidateDetail,
  parseCandidateStatus,
} from '@/modules/candidate-detail/utils/candidateBrief';
import { useRejectCandidateJobMutation } from '@/modules/candidates/mutations/useRejectCandidateJobMutation';
import type { CandidateJobApi } from '@/modules/candidates/types';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import type { CandidateStage } from '@/modules/hiring-manager/hooks/useCandidatePipelineQuery';
import { candidateSubmissionsAwaitingReviewQueryKeys } from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import {
  candidateCurrentWork,
  candidateFullName,
} from '@/modules/hiring-manager/utils/candidateDisplay';
import { PortalSidePanel } from '@/modules/layouts/components/PortalSidePanel';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { failureReason, reasonCopy } from '@/pages/hm/messages/outreachThread';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { getInitials } from '@/utils/getInitials';
import { invalidateQuery } from '@/utils/queries';

import {
  experienceMonths,
  formatStageAge,
  formatYears,
} from './candidateFacts';
import { stages } from './candidateStages';
import { CandidateStatus } from './CandidateTable';

/** Where the open candidate sits among the rows loaded in the table. */
export interface CandidatePanelPosition {
  index: number;
  total: number;
  previousId?: string;
  nextId?: string;
}

interface CandidatePanelProps {
  jobId: string;
  candidateId: string;
  /**
   * The candidate's table row, for the stage and its timing. Absent when the
   * candidate is not among the loaded rows, such as right after a rejection
   * moved them out of every tab.
   */
  row?: { candidateJob: CandidateJobApi; stage: CandidateStage };
  position?: CandidatePanelPosition;
  /** See `CandidateTableProps.showFailureReasons`. */
  showFailureReason: boolean;
  /** Query string to carry through internal links (tenant selection). */
  searchSuffix: string;
  onNavigate: (candidateId: string) => void;
  onClose: () => void;
}

/**
 * A candidate beside the table: who they are, where they stand, the
 * actions of the full profile, and Finlay's assessment. The brief owns the
 * panel's loading and errors; the table row adds the stage timing and draws
 * the header while the brief loads.
 */
export function CandidatePanel({
  jobId,
  candidateId,
  row,
  position,
  showFailureReason,
  searchSuffix,
  onNavigate,
  onClose,
}: CandidatePanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const params = { jobId, candidateId };
  const briefQuery = useCandidateBriefQuery(params);
  const rejectMutation = useRejectCandidateJobMutation();
  const previousId = position?.previousId;
  const nextId = position?.nextId;

  // Move focus in when the panel opens, not on every Previous/Next.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // The panel stays mounted across Previous/Next: each candidate starts at
  // the top, not where the last one was scrolled to.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [candidateId]);

  // Previous/Next open instantly: their briefs load while this one is read.
  useEffect(() => {
    for (const id of [previousId, nextId]) {
      if (id) {
        void queryClient.prefetchQuery(
          candidateBriefQueryOptions({ jobId, candidateId: id }),
        );
      }
    }
  }, [queryClient, jobId, previousId, nextId]);

  // Escape closes the panel and J/K step through the rows wherever focus is,
  // as long as nothing on top of it takes the key first: Radix sheets and
  // menus listen in the capture phase and mark the Escape they handle as
  // default-prevented. Letters typed into a field or a dialog stay text.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (
        event.target instanceof Element &&
        event.target.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="dialog"], [role="alertdialog"], [role="menu"]',
        )
      ) {
        return;
      }
      const targetId =
        event.key === 'j' ? nextId : event.key === 'k' ? previousId : undefined;
      if (targetId) onNavigate(targetId);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, onNavigate, previousId, nextId]);

  const brief = briefQuery.data;
  const fromRow = row ? rowIdentity(row.candidateJob) : undefined;
  const briefCandidate = brief ? mapCandidateDetail(brief) : undefined;
  // The row is there before the brief, so the header never blanks while
  // stepping; its work history is also fresher than the brief's headline.
  const identity: PanelIdentityProps | undefined = briefCandidate
    ? {
        name: briefCandidate.name,
        currentRole: fromRow?.currentRole ?? briefCandidate.currentRole,
        location: briefCandidate.location,
        experience: fromRow?.experience ?? briefCandidate.totalExperience,
        linkedInUrl: briefCandidate.linkedInUrl,
      }
    : fromRow;

  const reject = (rejectionMessage: string | null) =>
    rejectMutation.mutate(
      { ...params, message: rejectionMessage },
      {
        // The decision area updating is the confirmation; only a failure toasts.
        onSuccess: () =>
          Promise.all([
            briefQuery.refetch(),
            invalidateQuery(candidateSubmissionsAwaitingReviewQueryKeys.all)(),
          ]),
        onError: (error) =>
          toast.error(actionErrorMessage('reject the candidate', error)),
      },
    );

  return (
    <PortalSidePanel
      aria-label="Candidate"
      onClose={onClose}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="flex h-12 shrink-0 items-center gap-1 border-b px-4">
        {position && (
          <>
            <NavButton
              direction="previous"
              onNavigate={onNavigate}
              targetId={position.previousId}
            />
            <NavButton
              direction="next"
              onNavigate={onNavigate}
              targetId={position.nextId}
            />
            <span className="ml-1 text-[13px] text-neutral-500 tabular-nums">
              {position.index + 1} of {position.total}
            </span>
          </>
        )}
        <Link
          className="text-primary-700 ml-auto inline-flex items-center gap-1 rounded-sm px-1 text-[13px] font-medium hover:underline"
          to={`/hm/jobs/${jobId}/candidates/${candidateId}${searchSuffix}`}
        >
          Open full profile
          <ArrowRight className="size-3.5" />
        </Link>
        <button
          aria-label="Close candidate"
          className="text-muted-foreground hover:bg-accent hover:text-foreground ml-2 grid size-10 cursor-pointer place-items-center rounded-md lg:size-8"
          onClick={onClose}
          type="button"
        >
          <X className="size-4" />
        </button>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-6 pt-6 pb-10"
        ref={scrollRef}
      >
        {brief === undefined && briefQuery.isError ? (
          <HMLoadError
            description="We could not load this candidate. Check your connection and try again."
            isRetrying={briefQuery.isFetching}
            onRetry={() => void briefQuery.refetch()}
            title="Candidate unavailable"
          />
        ) : (
          <div className="flex flex-col gap-6">
            {briefQuery.isError && (
              <HMLoadError
                description="We could not refresh this candidate. Showing the previously loaded information."
                isRetrying={briefQuery.isFetching}
                onRetry={() => void briefQuery.refetch()}
                title="Candidate may be out of date"
              />
            )}
            {identity ? <PanelIdentity {...identity} /> : <IdentitySkeleton />}
            {brief ? (
              <PanelBrief
                brief={brief}
                jobId={jobId}
                // Previous/Next keep the panel mounted: a fresh brief per
                // candidate, so an open "Confirm reject" never carries over.
                key={candidateId}
                onReject={reject}
                pendingAction={rejectMutation.isPending ? 'reject' : null}
                rejected={
                  rejectMutation.isSuccess &&
                  rejectMutation.variables.candidateId === candidateId
                }
                row={row}
                searchSuffix={searchSuffix}
                showFailureReason={showFailureReason}
              />
            ) : (
              <BriefSkeleton />
            )}
          </div>
        )}
      </div>
    </PortalSidePanel>
  );
}

interface PanelBriefProps {
  brief: CandidateBriefApi;
  jobId: string;
  row: CandidatePanelProps['row'];
  searchSuffix: string;
  showFailureReason: boolean;
  pendingAction: PendingDecisionAction;
  /**
   * The rejection call succeeded: show it before the brief refetch lands.
   * The panel stays mounted across candidates, so this is per candidate.
   */
  rejected: boolean;
  onReject: (rejectionMessage: string | null) => void;
}

/** Where the candidate stands, the actions, and Finlay's assessment. */
function PanelBrief({
  brief,
  jobId,
  row,
  searchSuffix,
  showFailureReason,
  pendingAction,
  rejected,
  onReject,
}: PanelBriefProps) {
  const candidate = mapCandidateDetail(brief);
  const status = parseCandidateStatus(brief.status);
  const decision = rejected ? 'rejected' : decisionFromStatus(status);
  const params = { jobId, candidateId: candidate.candidateId };
  // Name the stage only where the chip shows a step within it ("Draft · not
  // sent" in Outreach); elsewhere the chip already says it.
  const stageTitle =
    row?.stage === 'outreach' || row?.stage === 'prescreening'
      ? stages.find((stage) => stage.key === row.stage)?.title
      : undefined;
  const stageAge = row?.candidateJob.stateEnteredAt
    ? formatStageAge(row.candidateJob.stateEnteredAt)
    : undefined;
  // Spelled out here too: the chip's tooltip does not open on touch.
  const reachOutMessage = row?.candidateJob.reachOutMessage;
  const failure =
    showFailureReason &&
    row?.stage === 'outreach' &&
    reachOutMessage?.status === 'Failed'
      ? reasonCopy[failureReason(reachOutMessage)]
      : undefined;

  return (
    <>
      <div className="flex flex-col gap-3 rounded-[10px] border bg-neutral-50 p-3.5">
        {row && (
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <CandidateStatus
              candidateJob={row.candidateJob}
              showFailureReason={showFailureReason}
              stage={row.stage}
            />
            <span className="text-[13px] text-neutral-500">
              {[stageTitle, stageAge && `${stageAge} in stage`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        )}
        {failure && (
          <p className="text-[13px] text-neutral-600">
            <span className="text-foreground font-medium">
              {failure.title}.
            </span>{' '}
            {failure.hint}
          </p>
        )}
        <div className="flex min-h-8 flex-wrap items-center gap-2">
          <CandidateDraftReview
            decision={decision}
            params={params}
            pendingAction={pendingAction}
            searchSuffix={searchSuffix}
          />
          <CandidateDecisionArea
            decision={decision}
            jobId={jobId}
            candidateId={candidate.candidateId}
            firstName={candidate.firstName}
            isSelfServiceJob={candidate.isSelfServiceJob}
            onReject={onReject}
            pendingAction={pendingAction}
            status={status}
          />
        </div>
      </div>

      <CandidateSummary candidate={candidate} />
    </>
  );
}

/** What the table row already knows, so the header shows before the brief. */
function rowIdentity(candidateJob: CandidateJobApi) {
  const months = experienceMonths(candidateJob.candidate.workExperiences ?? []);
  return {
    name: candidateFullName(candidateJob),
    currentRole: candidateCurrentWork(candidateJob),
    location: candidateJob.candidate.location,
    experience:
      months === undefined ? undefined : `${formatYears(months)} experience`,
  };
}

interface PanelIdentityProps {
  name: string;
  currentRole?: string;
  location?: string | null;
  experience?: string | null;
  /** Only the brief has it; it joins the line once the brief loads. */
  linkedInUrl?: string | null;
}

function PanelIdentity({
  name,
  currentRole,
  location,
  experience,
  linkedInUrl,
}: PanelIdentityProps) {
  return (
    <div className="flex items-start gap-3.5">
      <span className="bg-secondary-150 text-primary-700 grid size-12 shrink-0 place-items-center rounded-full text-sm font-semibold tracking-wide">
        {getInitials(name)}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 className="text-lg leading-6 font-semibold tracking-[-0.01em]">
          {name}
        </h2>
        {currentRole && (
          <p className="text-[13px] text-neutral-600">{currentRole}</p>
        )}
        <p className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-neutral-500">
          {[location, experience].filter(Boolean).join(' · ')}
          {linkedInUrl && (location || experience) && (
            <span aria-hidden>·</span>
          )}
          {linkedInUrl && (
            <a
              className="text-primary-700 inline-flex items-center gap-1 font-medium hover:underline"
              href={linkedInUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              LinkedIn
              <ExternalLink aria-hidden className="size-3" />
            </a>
          )}
        </p>
      </div>
    </div>
  );
}

function NavButton({
  direction,
  targetId,
  onNavigate,
}: {
  direction: 'previous' | 'next';
  targetId?: string;
  onNavigate: (candidateId: string) => void;
}) {
  const Icon = direction === 'previous' ? ChevronUp : ChevronDown;
  const shortcut = direction === 'previous' ? 'K' : 'J';
  const label =
    direction === 'previous' ? 'Previous candidate' : 'Next candidate';
  return (
    <button
      aria-keyshortcuts={shortcut}
      aria-label={label}
      className="text-muted-foreground hover:bg-accent hover:text-foreground disabled:text-input grid size-10 cursor-pointer place-items-center rounded-md border disabled:cursor-default disabled:hover:bg-transparent lg:size-8"
      title={`${label} (${shortcut})`}
      disabled={!targetId}
      onClick={() => targetId && onNavigate(targetId)}
      type="button"
    >
      <Icon className="size-4" />
    </button>
  );
}

function IdentitySkeleton() {
  return (
    <div className="flex items-center gap-3.5">
      <Skeleton className="size-12 rounded-full" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-3.5 w-60" />
      </div>
    </div>
  );
}

function BriefSkeleton() {
  return (
    <div
      aria-label="Loading candidate"
      className="flex flex-col gap-6"
      role="status"
    >
      <Skeleton className="h-24 rounded-[10px]" />
      <Skeleton className="h-40" />
    </div>
  );
}
